import { getLocalizedDate } from '@shared/lib/localize';
import { formatPhoneForInfoPanel } from '@shared/lib/phone';
import { PATIENT_LANGUAGE_BY_KEY } from '@entities/patient';
import { formatAddress } from './formatAddress';

/**
 * Build the per-section row structure consumed by PatientPane.
 * Each section is { id, title, rows: InfoRowProps[] }.
 *
 * Behaviour preserved 1:1 from the legacy PatientInfo useMemo:
 *   - DS-only Business row in Identification.
 *   - Email row carries action: 'email' — the popover that fires
 *     mailto / Outlook deeplink lives inside InfoRow.
 *   - Address row uses multiline `info` + flat `customCopyValue`.
 *   - Anonymous patients see only the Business row (DS) or nothing.
 */
export const getPatientRows = ({ patient, i18n, isDistributionSponsor }) => {
  if (!patient) return [];
  const { forms, infoPane } = i18n;

  // Patient pane rows never flash on change — the change-indicator is
  // reserved for Metrics rows where live filter updates actually drive
  // value changes the clinician wants to notice.
  const withNoFlash = row => ({ ...row, noFlash: true });

  if (patient.isAnonymousPatient) {
    if (!isDistributionSponsor) return [];
    return [
      {
        id: 'identification',
        title: infoPane.sections.identification,
        rows: [
          withNoFlash({ title: forms.bussinessName, info: patient.accountBusinessName || '-' }),
        ],
      },
    ];
  }

  const address = formatAddress(patient.address);

  const identification = [
    ...(isDistributionSponsor
      ? [{ title: forms.bussinessName, info: patient.accountBusinessName || '-' }]
      : []),
    { title: forms.mrnNumber, info: patient.mrnNumber, mono: true },
    { title: forms.firstName, info: patient.firstName },
    { title: forms.lastName, info: patient.lastName },
  ].map(withNoFlash);

  // Patient Engagement master flag in its own section so a paused
  // patient is visible without scrolling (sections render top-down;
  // this one sits right under Identification). Missing flag (older
  // API) reads as enabled; reason + changed-by rows appear only when
  // a reason is on record. The status row switches to the critical
  // tone pill while engagement is off.
  const engagementOff = patient.engagementEnabled === false;
  const engagement = [
    {
      title: forms.patientEngagement,
      info: engagementOff
        ? forms.patientEngagementDisabledShort
        : forms.patientEngagementEnabledShort,
      ...(engagementOff ? { tone: 'critical' } : {}),
    },
    ...(patient.engagementStatusReason
      ? [
          {
            title: forms.patientEngagementReason,
            info: patient.engagementStatusReason,
          },
          {
            title: forms.patientEngagementChangedBy,
            info: `${patient.engagementStatusChangedByName || '-'}${
              patient.engagementStatusChangedAt
                ? `, ${getLocalizedDate(patient.engagementStatusChangedAt, 'UTC')}`
                : ''
            }`,
          },
        ]
      : []),
  ].map(withNoFlash);

  const contact = [
    {
      title: forms.phone,
      info: patient?.phone?.phone ? formatPhoneForInfoPanel(patient.phone.phone) : '-',
      mono: true,
    },
    {
      title: forms.email,
      info: patient.email,
      action: 'email',
      dataActionTooltipTitle: infoPane.sentEmail,
    },
    {
      title: forms.address,
      info: address.lines.length ? address.lines : '',
      customCopyValue: address.copyValue,
      multiline: true,
    },
  ].map(withNoFlash);

  const demographics = [
    { title: forms.dateOfBirth, info: getLocalizedDate(patient.dateOfBirth, 'UTC'), mono: true },
    { title: forms.genderAtBirth, info: forms[patient.genderAtBirth] },
    {
      title: forms.drugAllergies,
      info: patient.hasDrugAllergies ? forms.yes : forms.no,
      badge: true,
    },
    { title: forms.patientLanguage, info: PATIENT_LANGUAGE_BY_KEY[patient.patientLanguage] },
  ].map(withNoFlash);

  return [
    { id: 'identification', title: infoPane.sections.identification, rows: identification },
    { id: 'engagement', title: infoPane.sections.engagement, rows: engagement },
    { id: 'contact', title: infoPane.sections.contact, rows: contact },
    { id: 'demographics', title: infoPane.sections.demographics, rows: demographics },
  ];
};
